import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST } from '../../../../constants/common'
import { useLocationPagination } from '../../../Pagination'
import { RENTAL_PORTAL_PAGE_READ_EMPTY_COUNT } from '../../RentalPortal.constants'
import {
  getIsRentalPortalPageMoved,
  getIsRentalPortalPageReadCurrent,
  getRentalPortalPageReadKey,
} from '../../RentalPortal.utils'

// Paged read for the lists whose backend serves no table config yet; once it
// does, the list moves to useAsyncTable
export function useRentalPortalPageRead({
  isEnabled = true,
  isUrlBound,
  pageSize,
  read,
  readParams,
}) {
  const { pagination: locationPagination, setPagination } =
    useLocationPagination({ isUrlBound })

  const { page } = locationPagination

  const [retryCount, setRetryCount] = useState(0)

  const [result, setResult] = useState(null)

  const readIdRef = useRef(0)

  const readKey = getRentalPortalPageReadKey([
    ...readParams,
    isEnabled,
    page,
    retryCount,
  ])

  function changePage(nextPage) {
    setPagination({ ...locationPagination, page: nextPage })
  }

  function loadPage() {
    readIdRef.current += 1

    const readId = readIdRef.current

    read({ page, size: pageSize })
      .then(({ data }) => {
        if (getIsRentalPortalPageReadCurrent({ readId, readIdRef })) {
          setResult({ data, hasError: false, key: readKey })

          if (getIsRentalPortalPageMoved({ data, page })) {
            changePage(data.pagination.page)
          }
        }
      })
      .catch(() => {
        if (getIsRentalPortalPageReadCurrent({ readId, readIdRef })) {
          setResult(prevResult => ({
            ...prevResult,
            hasError: true,
            key: readKey,
          }))
        }
      })
  }

  useEffect(() => {
    if (isEnabled) {
      loadPage()
    }

    return () => {
      readIdRef.current += 1
    }
  }, [readKey])

  function handleSetPagination(nextPagination) {
    changePage(nextPagination.page)
  }

  function handleRetry() {
    setRetryCount(prevCount => prevCount + 1)
  }

  return {
    changePage,
    data: result?.data,
    handleRetry,
    handleSetPagination,
    hasError: result?.hasError,
    isInitiated: Boolean(result),
    isLoading: result?.key !== readKey,
    items: result?.data?.items ?? EMPTY_LIST,
    pagination: {
      count:
        result?.data?.pagination?.total ?? RENTAL_PORTAL_PAGE_READ_EMPTY_COUNT,
      page,
      pageCount:
        result?.data?.pagination?.page_count ??
        RENTAL_PORTAL_PAGE_READ_EMPTY_COUNT,
      pageSize,
    },
  }
}
